import React, { memo, useEffect } from 'react';
import { App, Divider, Space, theme } from 'antd';
import { useGetScreenSize } from '../../common/hooks/useGetScreenSize';
import { useAppStore } from '../../common/store';
import { DocgenClient } from '../../data/docgenClient';
import { PatchPayloadItem } from '../../data/interfaces/APIRequest';
import { updateFieldsOfVariable } from '../../data/variablesDataApi';
import { startPathSlug } from '../../routes';
import { t } from '../../utils';
import { navigationEventManager } from '../../utils/navigationEventManager';
import { validateRoles, validateVariables } from '../../utils/validator';
import DocumentMetaData from './DocumentMetaData';
import { SelectDocument } from './SelectDocument';

const useDocgenNavigation = () => {
	const { notification } = App.useApp();
	const { documentTemplate, setIsLoading, updateTemplateMetadata } = useAppStore(
		state => ({
			documentTemplate: state.documentTemplate,
			setIsLoading: state.setIsLoading,
			updateTemplateMetadata: state.updateTemplateMetadata,
		})
	);

	if (documentTemplate?.rid) {
		const navigationEventObject = {
			async onNext() {
				const {
					// eslint-disable-next-line @typescript-eslint/no-shadow
					documentTemplate,
					updateDocumentTemplate,
					downloadUrl,
					fetchDownloadUrl,
				} = useAppStore.getState();

				const roles = documentTemplate.roles;
				const variables = documentTemplate.variables;

				const roleErrorMessage = validateRoles(roles);
				const variableErrorMessage = validateVariables(variables);

				if (!documentTemplate?.file?.fileName) {
					notification.error({
						message: t('uploadDocumentErrorMessage'),
					});
				} else if (roles && roleErrorMessage !== '') {
					notification.error({
						message: roleErrorMessage,
					});
				} else if (variableErrorMessage !== '') {
					notification.error({
						message: variableErrorMessage,
					});
				} else {
					try {
						setIsLoading(true);
						if (!downloadUrl) {
							await fetchDownloadUrl();
						}

						const payload: PatchPayloadItem[] = [
							{
								op: 'replace',
								path: '/roles',
								value: documentTemplate?.roles,
							},
							{
								op: 'replace',
								path: '/fields',
								value: updateFieldsOfVariable(
									documentTemplate?.fields,
									documentTemplate?.variables
								),
							},
							{
								op: 'replace',
								path: '/signerSequencing',
								value: documentTemplate.signerSequencing,
							},
							{
								op: 'replace',
								path: '/variables',
								value: documentTemplate?.variables,
							},
						];

						const patchResponse = await DocgenClient.updateDocumentTemplate(
							payload,
							documentTemplate.rid
						);

						updateDocumentTemplate(patchResponse);
						updateTemplateMetadata({ currentStep: 1 });
					} catch (error: any) {
						notification.error({
							message: t('errorMessages.updateDocumentTemplateError'),
						});
					} finally {
						setIsLoading(false);
					}
				}
			},
			onPrevious() {},
		};

		navigationEventManager.register(startPathSlug, navigationEventObject);
	}
};

export const Start = memo(() => {
	const { notification } = App.useApp();
	const { token } = theme.useToken();

	const { documentTemplate, fetchDownloadUrl, downloadUrl } = useAppStore(state => ({
		documentTemplate: state.documentTemplate,
		fetchDownloadUrl: state.fetchDownloadUrl,
		downloadUrl: state.downloadUrl,
	}));
	useDocgenNavigation();
	const { isExtraSmallScreen } = useGetScreenSize();

	useEffect(() => {
		if (documentTemplate?.file && !downloadUrl) {
			try {
				fetchDownloadUrl();
			} catch (error: unknown) {
				notification.error({
					message: t('genericError'),
				});
			}
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [documentTemplate?.file, downloadUrl]);

	return (
		<Space
			size={documentTemplate?.file?.fileName ? 'small' : 'large'}
			direction="vertical"
			style={{
				paddingInline: isExtraSmallScreen ? token.paddingMD : token.paddingLG,
				paddingBlockEnd: isExtraSmallScreen ? token.paddingMD : token.paddingLG,
			}}
		>
			<SelectDocument />
			{documentTemplate?.file?.fileName && <Divider />}
			<DocumentMetaData />
		</Space>
	);
});
